import { InboxOutlined }        from '@ant-design/icons';
import { notification }         from 'antd';
import { Upload }               from 'antd';
import { Form }                 from 'antd';
import { AppFormInjectedProps } from 'components/App/AppForm';
import FormattedMessageWrapper  from 'components/App/FormattedMessageWrapper';
import IntlInput                from 'components/App/IntlInput';
import RichTextEditor           from 'components/App/RichTextEditor';
import { FileConnector }        from 'connectors/FileConnector';
import ReportMediaModel         from 'models/ReportMediaModel';
import ReportModel              from 'models/ReportModel';
import StandModel               from 'models/StandModel';
import { useEffect }            from 'react';
import { useImperativeHandle }  from 'react';
import React                    from 'react';
import handleFormError          from 'utils/handleFormError';
import { formatMessageIntl }    from 'utils/messageIntl';
import { messageErrorIntl }     from 'utils/messageIntl';

const { Dragger } = Upload;

interface ReportFormExternalProps {
  onSuccess?: (report: ReportModel) => void;
  report: ReportModel;
  reportMedias: ReportMediaModel[];
  stand: StandModel;
}

type ReportFormProps = ReportFormExternalProps & AppFormInjectedProps;

export const ReportForm = React.forwardRef(({
  onSuccess,
  report,
  reportMedias,
  setSaveButtonProps,
  stand,
}: ReportFormProps, ref) => {
  const [form] = Form.useForm();

  const [attachments, setAttachments] = React.useState<string[]>([]);

  useEffect(() => {
    const initialFiles = reportMedias.map(media => ({
      uid: media.get('@id'), // L'ID unique pour Antd
      '@id': media.get('@id'), // On garde l'IRI pour la logique onRemove
      name: media.mediaFileName || 'Image',
      status: 'done',
      url: media.objectUrl,
    }));

    const initialIris = reportMedias.map(media => media.get('@id'));

    setAttachments(initialIris);

    form.setFieldsValue({
      attachmentsList: initialFiles,
      comment: report.comment,
      name: report.name,
    });
  }, [form, report, reportMedias]);

  useImperativeHandle(ref, () => ({
    submit: () => {
      form.submit(); // Déclenche la validation d'Ant Design
    }
  }));

  const onFinish = async (values: any) => {
    setSaveButtonProps({ loading: true });

    try {
      await report.patch({
        attachments,
        comment: values.comment,
        name: values.name,
        stand: stand.iri,
      });

      if (onSuccess) {
        onSuccess(values);
      }
    } catch (error) {
      handleFormError(error, form);
    } finally {
      setSaveButtonProps({ loading: false });
    }
  };

  const handleUpload = async (file: any) => {
    const currentList = form.getFieldValue('attachmentsList') || [];

    if (attachments.length >= 4) {
      messageErrorIntl("4 photos ont déjà été téléchargées.");
      return Upload.LIST_IGNORE;
    }

    const reportMedia = new ReportMediaModel();
    reportMedia.set({
      enabled: true,
      file,
    });

    try {
      const response = await reportMedia.save();

      // const newIri = reportMedia.iri; // this is not working, don't know why
      const newIri = (response.connector as FileConnector).response.data['@id'];

      setAttachments(prev => [...prev, newIri]);

      form.setFieldsValue({
        attachmentsList: [...currentList, {
          ...file,
          uid: file.uid, // Antd utilise uid pour le suivi interne
          '@id': newIri,
          name: reportMedia.mediaFileName || file.name,
          status: 'done',
        }]
      });

    } catch {
      notification.error({
        title: formatMessageIntl("Le chargement du fichier a échoué.")
      });
    }

    return false;
  };

  const handleRemove = (file: any) => {
    const iriToRemove = file['@id'];

    setAttachments(prev => prev.filter(id => id !== iriToRemove));

    const currentList = form.getFieldValue('attachmentsList') || [];

    form.setFieldsValue({
      attachmentsList: currentList.filter((f: any) => f.uid !== file.uid),
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      <Form.Item
        label={<FormattedMessageWrapper id="Personne en charge du constat :" />}
        name="name"
        rules={[
          {
            message: <FormattedMessageWrapper id="Information manquante" />,
            required: true,
          },
        ]}
      >
        <IntlInput placeholder="Nom" />
      </Form.Item>

      <Form.Item
        label={<FormattedMessageWrapper id="Commentaire :" />}
        name="comment"
      >
        <RichTextEditor style={{ height: 120, marginBottom: 32 }} />
      </Form.Item>

      <Form.Item
        extra={<FormattedMessageWrapper id="Jusqu'à 4 photos peuvent être ajoutées." />}
        label={<FormattedMessageWrapper id="Photos :" />}
      >
        <Form.Item
          name="attachmentsList"
          valuePropName="fileList"
          getValueFromEvent={(e: any) => (Array.isArray(e) ? e : e?.fileList)}
          noStyle
        >
          <Dragger
            multiple
            beforeUpload={handleUpload}
            onRemove={handleRemove}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              <FormattedMessageWrapper id="Ajouter une ou plusieurs photos" />
            </p>
            <p className="ant-upload-hint">
              <FormattedMessageWrapper id="Cliquez-ici ou déposez une ou plusieurs photos." />
            </p>
          </Dragger>
        </Form.Item>
      </Form.Item>

      {/*utilisé pour stocker les ids*/}
      <Form.Item hidden name="attachments">
        <IntlInput />
      </Form.Item>
    </Form>
  );
});